/**
 * Φόρμα κατηγορίας στο ίδιο πάνελ (✎ στο δέντρο): τα πεδία του παλιού παραθύρου κατηγορίας. Η σειρά
 * αλλάζει με σύρσιμο στο δέντρο (όχι με αριθμό)· ο γονέας και από εδώ, ένα επίπεδο όπως σήμερα.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, Plus, Trash2, X } from 'lucide-react';
import CatalogColorPicker from '../shared/CatalogColorPicker';
import type { CatalogTone } from '../../utils/catalogColors';
import { CategoryForm, GroupRef, VatCategory } from './catalogModel';
import type { GroupInfo } from './ProductOptionsTab';
import { cx, MenuButton, Switch, ToneDot } from './ui';

export interface CategoryPanelProps {
  categoryKey: string;
  form: CategoryForm;
  isNew: boolean;
  parentKey: string | null;
  parentOptions: Array<{ key: string; name: string }>;
  hasChildren: boolean;
  tone: CatalogTone;
  autoTone: CatalogTone;
  groups: GroupRef[] | null;
  library: GroupInfo[];
  vatCategories: VatCategory[];
  changed: Set<string>;
  readOnly: boolean;
  floating: boolean;
  parentName: string | null;
  onChange: (patch: Partial<CategoryForm>) => void;
  onParent: (parentKey: string | null) => void;
  onGroups: (groups: GroupRef[]) => void;
  onClose: () => void;
  onDiscard: () => void;
}

const CategoryPanel: React.FC<CategoryPanelProps> = props => {
  const { t } = useTranslation(['catalog']);
  const { form, readOnly, changed } = props;
  const mark = (field: string) => (changed.has(field) ? 'cx-changed' : '');
  const linked = props.groups || [];
  const byRef = new Map(props.library.map(g => [String(g.ref), g] as [string, GroupInfo]));
  const unlinked = props.library.filter(g => !linked.some(r => String(r) === String(g.ref)));
  const name = form.name || t('catalog:category.untitled');

  return (
    <aside className={cx('cx-pane cx-panel', props.floating && 'floating')} aria-label={t('catalog:category.panelAria', { name })}>
      <div className="cx-panel-head">
        <div className="cx-ph-text">
          <div className="cx-crumb">{props.parentName ? t('catalog:category.subOf', { name: props.parentName }) : t('catalog:category.kind')}</div>
          <h2><ToneDot tone={props.tone} large /><span className="cx-title-text">{name}</span>{props.isNew && <span className="cx-badge brand">{t('catalog:status.new')}</span>}</h2>
        </div>
        <button type="button" className="cx-icon-btn" onClick={props.onClose} aria-label={t('catalog:common.close')} title={t('catalog:common.close')}>
          <X className="cx-ico" />
        </button>
      </div>
      <div className="cx-panel-body">
        <fieldset disabled={readOnly}>
          <div className="cx-form">
            <div className="cx-field">
              <label htmlFor="cat-name">{t('catalog:fields.name')}</label>
              <input id="cat-name" className={cx('cx-inp', mark('name'))} value={form.name} autoFocus={props.isNew}
                placeholder={t('catalog:category.namePlaceholder')} onChange={e => props.onChange({ name: e.target.value })} />
            </div>
            <div className="cx-field">
              <label htmlFor="cat-desc">{t('catalog:fields.description')} <span className="cx-opt">{t('catalog:common.optional')}</span></label>
              <input id="cat-desc" className={cx('cx-inp', mark('description'))} value={form.description}
                onChange={e => props.onChange({ description: e.target.value })} />
            </div>
            <div className="cx-field">
              <label htmlFor="cat-parent">{t('catalog:fields.parent')}</label>
              <select id="cat-parent" className={cx('cx-inp', mark('parent'))} value={props.parentKey || ''} disabled={readOnly || props.hasChildren}
                onChange={e => props.onParent(e.target.value || null)}>
                <option value="">{t('catalog:category.rootOption')}</option>
                {props.parentOptions.map(o => <option key={o.key} value={o.key}>{o.name}</option>)}
              </select>
              <p className="cx-hint">{props.hasChildren ? t('catalog:category.parentLocked') : t('catalog:category.parentHint')}</p>
            </div>
            <div className="cx-field">
              {changed.has('color') && <span className="cx-edot" />}
              <CatalogColorPicker kind="category" idPrefix="cat-color" value={form.color} autoTone={props.autoTone}
                onChange={tone => !readOnly && props.onChange({ color: tone })} />
            </div>
            <div className="cx-sw-row">
              <Switch checked={form.is_hidden} disabled={readOnly} label={t('catalog:fields.categoryHidden')} pending={changed.has('is_hidden')}
                onChange={next => props.onChange({ is_hidden: next })} />
              <span><b>{t('catalog:fields.categoryHidden')}</b><span className="cx-hint">{t('catalog:category.hiddenHint')}</span></span>
            </div>

            <div className="cx-sec-title sm" style={{ marginTop: 4 }}>{t('catalog:category.defaultsTitle')}</div>
            <div className="cx-grid2">
              <div className="cx-field">
                <label htmlFor="cat-type">{t('catalog:fields.type')}</label>
                <select id="cat-type" className={cx('cx-inp', mark('default_product_type'))} value={form.default_product_type}
                  onChange={e => props.onChange({ default_product_type: e.target.value as CategoryForm['default_product_type'] })}>
                  <option value="">{t('catalog:category.noneOption')}</option>
                  <option value="ΕΜΠΟΡΕΥΜΑ">{t('catalog:types.goodsLong')}</option>
                  <option value="ΠΡΟΙΟΝ">{t('catalog:types.productLong')}</option>
                  <option value="ΥΠΗΡΕΣΙΑ">{t('catalog:types.serviceLong')}</option>
                </select>
              </div>
              <div className="cx-field">
                <label htmlFor="cat-vat">{t('catalog:fields.vat')}</label>
                <select id="cat-vat" className={cx('cx-inp', mark('default_vat'))} value={form.default_vat ?? ''}
                  onChange={e => props.onChange({ default_vat: e.target.value === '' ? null : Number(e.target.value) })}>
                  <option value="">{t('catalog:category.noneOption')}</option>
                  {form.default_vat !== null && !props.vatCategories.some(vc => Number(vc.rate) === form.default_vat) && (
                    <option value={form.default_vat}>{form.default_vat}%</option>
                  )}
                  {props.vatCategories.map(vc => <option key={vc.id} value={Number(vc.rate)}>{Number(vc.rate)}%{vc.name ? ` (${vc.name})` : ''}</option>)}
                </select>
              </div>
            </div>
            <div className="cx-field">
              <span className="cx-lbl">{t('catalog:fields.defaultGroups')}{changed.has('groups') && <span className="cx-edot" style={{ marginLeft: 6 }} />}</span>
              {props.groups === null ? (
                <span className="cx-hint" role="status"><Loader2 className="cx-ico animate-spin" style={{ display: 'inline' }} /> {t('catalog:common.loading')}</span>
              ) : (
                <div className="cx-chips">
                  {linked.map(ref => (
                    <span key={String(ref)} className="cx-chip">
                      {byRef.get(String(ref))?.name || `#${ref}`}
                      {!readOnly && (
                        <button type="button" aria-label={t('catalog:options.unlink', { name: byRef.get(String(ref))?.name || ref })}
                          onClick={() => props.onGroups(linked.filter(r => String(r) !== String(ref)))}>
                          <X className="cx-ico" style={{ width: 13, height: 13 }} />
                        </button>
                      )}
                    </span>
                  ))}
                  {!readOnly && (
                    <MenuButton label={t('catalog:category.addGroup')} className="cx-chip add" disabled={unlinked.length === 0}
                      items={unlinked.map(g => ({ key: String(g.ref), label: g.name, onSelect: () => props.onGroups([...linked, g.ref]) }))}>
                      <Plus className="cx-ico" style={{ width: 13, height: 13 }} /> {t('catalog:category.groupChip')} <ChevronDown className="cx-ico" style={{ width: 13, height: 13 }} />
                    </MenuButton>
                  )}
                </div>
              )}
              <p className="cx-hint">{t('catalog:category.groupsHint')}</p>
            </div>
            {props.isNew && !readOnly && (
              <div className="cx-danger-zone">
                <button type="button" className="cx-btn sm danger" onClick={props.onDiscard}><Trash2 className="cx-ico" /> {t('catalog:category.discardNew')}</button>
                <span className="cx-hint">{t('catalog:category.discardNewHint')}</span>
              </div>
            )}
          </div>
        </fieldset>
      </div>
    </aside>
  );
};

export default CategoryPanel;
